import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { LoadingIndicator } from '@moia-dev/pace-web'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { RouteBuilderAreaRoutes } from '../../RouteBuilderAreaRoutes'
import { useRouteBuilderSavedTripsByVtrsQuery } from '../SavedTrips/SavedTrips.hook'
import { useRouteBuilderVtrDataLazyQuery } from './TripEditor.hook'
import { TripEditorView } from './TripEditorView'
import { VtrSelectionModal } from './VtrSelectionModal'
import { VtrTicketSelector } from './VtrTicketSelector'

type CreateFromVtrViewProps = {
  metropolisId: number
  metropolisName: string | undefined
  mapCenter: [number, number]
}

export const CreateFromVtrView = ({
  metropolisId,
  metropolisName,
  mapCenter,
}: CreateFromVtrViewProps) => {
  const navigate = useNavigate()
  const [selectedTicketKey, setSelectedTicketKey] = useState<string | null>(
    null,
  )
  // Index of the CB requirement confirmed in the selection modal
  const [confirmedCbIndex, setConfirmedCbIndex] = useState<number | null>(null)

  const [fetchVtrData, { data: vtrDataResult, loading }] =
    useRouteBuilderVtrDataLazyQuery()

  const { data: savedTripsData } = useErrorHandlingQuery(
    useRouteBuilderSavedTripsByVtrsQuery,
    {
      variables: {
        associatedVtrs: selectedTicketKey ? [selectedTicketKey] : [],
      },
      skip: !selectedTicketKey,
    },
  )

  const similarRoutes = useMemo(() => {
    if (!selectedTicketKey || !savedTripsData?.savedTripsByVtrs) return []
    const entry = savedTripsData.savedTripsByVtrs.find(
      ({ vtr }) => vtr === selectedTicketKey,
    )
    return entry?.savedTrips.map(t => ({ id: t.id, name: t.name })) ?? []
  }, [selectedTicketKey, savedTripsData?.savedTripsByVtrs])

  const handleSelectTicket = (ticketKey: string) => {
    setSelectedTicketKey(ticketKey)
    setConfirmedCbIndex(null)
    fetchVtrData({ variables: { jiraTicketNumber: ticketKey } })
  }

  const handleConfirmCb = (cbIndex: number) => {
    setConfirmedCbIndex(cbIndex)
  }

  const handleCancelModal = () => {
    // Back to ticket selector
    setSelectedTicketKey(null)
    setConfirmedCbIndex(null)
  }

  const handleClearVtr = () => {
    navigate(RouteBuilderAreaRoutes.SavedTrips.route)
  }

  // Step 1: no ticket selected
  if (!selectedTicketKey) {
    return (
      <VtrTicketSelector
        metropolisName={metropolisName}
        onSelect={handleSelectTicket}
      />
    )
  }

  // Step 2: loading VTR data
  if (loading || !vtrDataResult?.vtrData) {
    return (
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <LoadingIndicator size={32} />
      </div>
    )
  }

  // Step 3: VTR loaded, CB not yet confirmed — show codebeamer selection modal
  if (confirmedCbIndex === null) {
    return (
      <VtrSelectionModal
        vtrData={vtrDataResult.vtrData}
        onConfirm={handleConfirmCb}
        onCancel={handleCancelModal}
      />
    )
  }

  // Step 4: CB confirmed — open the trip editor
  return (
    <TripEditorView
      metropolisId={metropolisId}
      metropolisName={metropolisName}
      mapCenter={mapCenter}
      vtr={{
        vtrData: vtrDataResult.vtrData,
        ticketKey: selectedTicketKey,
        selectedCbIndex: confirmedCbIndex,
        similarRoutes,
      }}
      onClearVtr={handleClearVtr}
    />
  )
}
